import React from 'react';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { formatDateTime, resolveCustomizationItemStatus } from '../utils/order';

function toneForItemStatus(status) {
  const value = String(status ?? '').toLowerCase();
  if (value.includes('ship')) return 'success';
  if (value.includes('cancel')) return 'danger';
  if (value.includes('pending') || value.includes('process') || value.includes('released')) return 'warning';
  return 'neutral';
}

export function CustomizationPanel({ context, loading, error }) {
  if (loading) return <div className="panel-loading">Loading customization context…</div>;
  if (error) return <div className="inline-error">{error}</div>;
  if (!context) return <div className="empty-state">Open this tab to load purpose-scoped customization data.</div>;

  const operations = context.customizationOperations;

  return (
    <div className="workspace-stack">
      <SectionCard title="Customization operations">
        {operations?.hasEmbroideryOperationsRecord ? (
          <>
            <div className="summary-strip">
              <SummaryItem label="Embroidery status" value={operations.embroideryStatus || 'Unknown'} />
              <SummaryItem label="Source hold flag" value={operations.isHold === true ? 'Yes' : operations.isHold === false ? 'No' : 'Not recorded'} />
              <SummaryItem label="Received" value={formatDateTime(operations.receivedDate)} />
              <SummaryItem label="Processed" value={formatDateTime(operations.processDate)} />
              <SummaryItem label="Closed" value={formatDateTime(operations.closedDate)} />
            </div>
            <div className="semantic-note">
              <strong>Source semantics:</strong> this hold flag is informational only. It does not establish why the order is on Hold and is not treated as an embroidery delay cause.
            </div>
          </>
        ) : <div className="empty-state compact">No order-level embroidery operations record is present.</div>}
      </SectionCard>

      <SectionCard title="Item customization">
        <div className="customization-grid">
          {(context.customizations ?? []).map((item) => {
            const currentItemStatus = resolveCustomizationItemStatus(
              item,
              context.items
            );

            return (
              <article className="customization-card" key={item.itemNo}>
              <div className="customization-card__header">
                <div>
                  <div className="eyebrow">Item {item.itemNo}</div>
                  <h3>{item.styleName || item.styleCode}</h3>
                  <p>{[item.colorName, item.sizeName].filter(Boolean).join(' · ')}</p>
                </div>
                <div className="customization-card__badges">
                  {currentItemStatus ? (
                    <StatusBadge tone={toneForItemStatus(currentItemStatus)}>{currentItemStatus}</StatusBadge>
                  ) : null}
                  {item.hasMTO ? <StatusBadge tone="warning">Made To Order</StatusBadge> : null}
                </div>
              </div>

              <div className="customization-facts">
                {item.hasMTO ? (
                  <>
                    <Fact label="Pockets" value={item.pockets} />
                    <Fact label="Sleeves" value={item.sleeves} />
                    <Fact label="Buttons" value={item.buttons} />
                  </>
                ) : null}
                {item.hasEmbroidery ? <Fact label="Embroidery" value="Yes" /> : null}
                {item.hasStockLogo ? <Fact label="Stock logo" value={[item.stockLogo1, item.stockLogo2].filter(Boolean).join(', ')} /> : null}
                {item.hasCustomLogo ? <Fact label="Custom logo" value={item.customLogoName || 'Recorded'} /> : null}
                {item.hasDigitizingRecord ? <Fact label="Digitizing status" value={item.digitizingStatus || 'Unknown'} /> : null}
              </div>
              </article>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

function SummaryItem({ label, value }) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function Fact({ label, value }) {
  return (
    <div className="fact-row">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
    </div>
  );
}